/**
 * Ledger filters, encoded in the URL so any view is shareable and bookmarkable.
 *
 * One shape, two directions: the same object drives the API request and the address bar.
 * The parameter names match the API exactly, so the query string *is* the API call.
 */

export type MembersMode = "any" | "all";

export type TxnTypeFilter = "expense" | "payment" | "adjustment" | "transfer_in" | "transfer_out";

export interface LedgerFilters {
  from?: string;
  to?: string;
  members: number[];
  membersMode: MembersMode;
  payer?: number;
  categories: number[];
  types: TxnTypeFilter[];
  currency?: string;
  min?: number;
  max?: number;
  q?: string;
  mine: boolean;
  lockedOnly: boolean;
  includeDeleted: boolean;
}

export const EMPTY_FILTERS: LedgerFilters = {
  members: [],
  membersMode: "any",
  categories: [],
  types: [],
  mine: false,
  lockedOnly: false,
  includeDeleted: false,
};

function numberList(raw: string | null): number[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => Number.parseInt(part, 10))
    .filter((value) => Number.isFinite(value));
}

function optionalNumber(raw: string | null): number | undefined {
  if (raw === null || raw.trim() === "") return undefined;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : undefined;
}

const TYPES: readonly TxnTypeFilter[] = [
  "expense",
  "payment",
  "adjustment",
  "transfer_in",
  "transfer_out",
];

export function parseFilters(params: URLSearchParams): LedgerFilters {
  const mode = params.get("members_mode");
  return {
    from: params.get("from") ?? undefined,
    to: params.get("to") ?? undefined,
    members: numberList(params.get("members")),
    membersMode: mode === "all" ? "all" : "any",
    payer: optionalNumber(params.get("payer")),
    categories: numberList(params.get("category")),
    types: (params.get("type") ?? "")
      .split(",")
      .filter((value): value is TxnTypeFilter => TYPES.includes(value as TxnTypeFilter)),
    currency: params.get("currency") ?? undefined,
    min: optionalNumber(params.get("min")),
    max: optionalNumber(params.get("max")),
    q: params.get("q") ?? undefined,
    mine: params.get("mine") === "true",
    lockedOnly: params.get("locked_only") === "true",
    includeDeleted: params.get("include_deleted") === "true",
  };
}

/** The canonical query string for a filter set. Empty values are omitted entirely. */
export function serializeFilters(filters: LedgerFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.members.length) {
    params.set("members", filters.members.join(","));
    if (filters.membersMode === "all") params.set("members_mode", "all");
  }
  if (filters.payer !== undefined) params.set("payer", String(filters.payer));
  if (filters.categories.length) params.set("category", filters.categories.join(","));
  if (filters.types.length) params.set("type", filters.types.join(","));
  if (filters.currency) params.set("currency", filters.currency);
  if (filters.min !== undefined) params.set("min", String(filters.min));
  if (filters.max !== undefined) params.set("max", String(filters.max));
  if (filters.q) params.set("q", filters.q);
  if (filters.mine) params.set("mine", "true");
  if (filters.lockedOnly) params.set("locked_only", "true");
  if (filters.includeDeleted) params.set("include_deleted", "true");
  return params;
}

/** How many filters are switched on — the "3 filters active" counter. */
export function activeFilterCount(filters: LedgerFilters): number {
  let count = 0;
  if (filters.from || filters.to) count += 1;
  if (filters.members.length) count += 1;
  if (filters.payer !== undefined) count += 1;
  if (filters.categories.length) count += 1;
  if (filters.types.length) count += 1;
  if (filters.currency) count += 1;
  if (filters.min !== undefined || filters.max !== undefined) count += 1;
  if (filters.q) count += 1;
  if (filters.mine) count += 1;
  if (filters.lockedOnly) count += 1;
  if (filters.includeDeleted) count += 1;
  return count;
}

/** The type filter's chips, in the order they read best. */
export const TYPE_LABELS: Record<TxnTypeFilter, string> = {
  expense: "Expenses",
  payment: "Payments",
  adjustment: "Adjustments",
  transfer_in: "Transfers in",
  transfer_out: "Transfers out",
};

export function isEmptyFilters(filters: LedgerFilters): boolean {
  return activeFilterCount(filters) === 0;
}

/** A calendar date as the date boxes show it: the viewer's own day, not UTC's. */
function localDate(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

export type DatePresetName = "last-week" | "this-month" | "last-30" | "this-year" | "all";

/**
 * Date presets for the range picker. "Last week" is the seven days before today and
 * today itself, matching the link the weekly summary notification opens.
 */
export function datePreset(
  name: DatePresetName,
  today: Date = new Date(),
): {
  from?: string;
  to?: string;
} {
  const daysAgo = (days: number) =>
    localDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - days));
  if (name === "last-week") return { from: daysAgo(7), to: undefined };
  if (name === "this-month") {
    return { from: localDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: undefined };
  }
  if (name === "last-30") return { from: daysAgo(30), to: undefined };
  if (name === "this-year") {
    return { from: localDate(new Date(today.getFullYear(), 0, 1)), to: undefined };
  }
  return { from: undefined, to: undefined };
}
